import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, createFilterOptions, FilterOptionsState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

export interface PartnerSellerInfo {
    name?: string;
    logo?: string;
    website?: string;
    description?: string;
}

export interface PartnerOption {
    id: string | number;
    sellerInfo?: PartnerSellerInfo;
    hasApproved?: boolean;
}

interface PartnersAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<PartnerOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    /** When set, skips the sellers-list request (e.g. import static preload). */
    cachedPartners?: PartnerOption[];

    [key: string]: any;
}

interface PartnerSellersListResponse {
    resultFlag?: boolean;
    data?: PartnerOption[];
}

const PartnersAutocomplete = <M extends boolean = false, >({
                                                               textFieldProps = {},
                                                               form = false,
                                                               name = undefined,
                                                               required = false,
                                                               setLoadingOptions: setLoadingOptionsProps,
                                                               cachedPartners,
                                                               ...props
                                                           }: PartnersAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * When setLoadingOptionsProps is provided, we call it with the current loading state whenever it changes. This allows parent components to be aware of the loading state of the options, which can be useful for showing loading indicators or disabling other UI elements while options are being fetched.
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * getOptions is a callback function that fetches the list of partner options. It first checks if there are cached partners provided via props; if not,
     * it makes an API call to fetch the list of sellers. It then filters the list based on the provided keywords, matching against the seller's name.
     * The loading state is managed using the loadingOptions state variable, and the function ensures that it only updates state if the component is still mounted to avoid memory leaks or errors.
     */
    const getOptions = useCallback(async (_cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            let list = cachedPartners;
            if (!list?.length) {
                const response = (await Api.partnerConnectionSellersList()) as PartnerSellersListResponse;
                if (!isMounted()) {
                    return [];
                }
                list = (response?.data ?? []).filter((e) => e.hasApproved);
            }
            const keyword = (keywords ?? '').trim().toLowerCase();
            if (!keyword) {
                return list ?? [];
            }
            return (list ?? []);
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [cachedPartners, isMounted]);

    /**
     * isOptionEqualToValue is a callback function that checks if a given option is equal to the currently selected value.
     */
    const isOptionEqualToValue = useCallback((option: PartnerOption, value: PartnerOption | null) => option.id === value?.id || (option.sellerInfo?.name != null && option.sellerInfo.name === value?.sellerInfo?.name), []);

    /**
     * getOptionLabel is a callback function that returns the label to be displayed for a given option.
     */
    const getOptionLabel = useCallback((option: PartnerOption) => option?.sellerInfo?.description ?? option?.sellerInfo?.name ?? String(option?.id ?? ''), []);

    /**
     * getOptionKey is a callback function that returns a unique key for a given option.
     */
    const getOptionKey = useCallback((option: PartnerOption) => String(option?.id ?? option?.sellerInfo?.description ?? option?.sellerInfo?.name ?? ''), []);

    const defaultOptions = useMemo(() => [], []);

    const defaultFilterOptions = useMemo(() => createFilterOptions<PartnerOption>({
        stringify: (option) => `${option.sellerInfo?.name ?? ''} ${option.sellerInfo?.description ?? ''} ${option.id ?? ''}`,
    }), []);

    /**
     * Custom filter options function that allows for free solo input. If the input value doesn't match any of the existing options, it will be added as a new option to the filtered list.
     */
    const filterOptions = useCallback((options: PartnerOption[], state: FilterOptionsState<PartnerOption>) => {
        return defaultFilterOptions(options, state);
    }, [defaultFilterOptions]);


    return (
        <AutocompleteBase<PartnerOption, M>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={300}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            filterOptions={filterOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default PartnersAutocomplete;
